<template>
  <CModal
    title="DNS Management Operations"
    color="white"
    size="lg"
    :show.sync="active"
    :closeOnBackdrop="false"
  >
    <div v-if="loading" class="text-center py-4">
      <CSpinner color="primary" />
      <p class="mt-2 text-muted">Loading DNS configuration...</p>
    </div>

    <div v-else>
      <div class="vertical-center justify-content-between mb-3">
        <h5>{{ restaurant?.restaurant_name }}</h5>
        <div class="text-muted vertical-center flex-gap-2">
          <CIcon name="cil-link" size="sm" />
          <a :href="selectedDomain | addUriScheme" target="_blank">{{ selectedDomain }}</a>
        </div>
      </div>

      <div>
        <h6 class="text-muted mb-3">Select operations to perform:</h6>

        <CAlert v-if="!pointToMenute && !restoreOvhDns && !useCustomNameservers && !allOperationsDisabled" color="warning" closeButton>
          <div class="d-flex align-items-center">
            <CIcon name="cil-warning" size="lg" class="mr-2" />
            <span>Please select at least one operation to perform.</span>
          </div>
        </CAlert>

        <CAlert v-if="allOperationsDisabled" color="success">
          <div class="d-flex align-items-center">
            <CIcon name="cil-check-circle" size="lg" class="mr-2" />
            <span>All DNS records are already configured correctly.</span>
          </div>
        </CAlert>
        
        <!-- Point to Menute Hosting -->
        <div 
          class="operation-card" 
          :class="{ 'selected': pointToMenute, 'disabled': isPointToMenuteDisabled }"
        >
          <CInputCheckbox
            :checked.sync="pointToMenute"
            :custom="true"
            :disabled="isPointToMenuteDisabled || useCustomNameservers"
            class="mb-0"
          >
            <template #label>
              <div class="d-flex flex-column" @click="!isPointToMenuteDisabled && (pointToMenute = !pointToMenute)">
                <strong class="mb-2">Point to Menute Hosting</strong>
                <small class="text-muted mb-2">Update A and AAAA records to Menute IP addresses</small>
                
                <div v-if="pointToMenute && dnsConfig" class="dns-preview mt-2" @click.stop>
                  <div v-if="dnsConfig.menute_ipv4.length > 0" class="mb-2">
                    <div class="dns-record-type">A Records (IPv4):</div>
                    <div class="dns-values">
                      <CInputCheckbox
                        v-for="(ip, idx) in dnsConfig.menute_ipv4"
                        :key="'ipv4-check-' + idx"
                        :custom="true"
                        :label="ip"
                        :checked="selectedIpv4.includes(ip)"
                        @update:checked="toggleIpSelection('selectedIpv4', ip)"
                        class="ip-checkbox"
                      />
                    </div>
                  </div>
                  
                  <div v-if="dnsConfig.menute_ipv6.length > 0">
                    <div class="dns-record-type">AAAA Records (IPv6):</div>
                    <div class="dns-values">
                      <CInputCheckbox
                        v-for="(ip, idx) in dnsConfig.menute_ipv6"
                        :key="'ipv6-check-' + idx"
                        :custom="true"
                        :label="ip"
                        :checked="selectedIpv6.includes(ip)"
                        @update:checked="toggleIpSelection('selectedIpv6', ip)"
                        class="ip-checkbox"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </CInputCheckbox>
        </div>

        <!-- Restore OVH DNS -->
        <div 
          class="operation-card mt-3" 
          :class="{ 'selected': restoreOvhDns, 'disabled': isRestoreOvhDisabled }"
        >
          <CInputCheckbox
            :checked.sync="restoreOvhDns"
            :custom="true"
            :disabled="isRestoreOvhDisabled || useCustomNameservers"
            class="mb-0"
          >
            <template #label>
              <div class="d-flex flex-column" @click="!isRestoreOvhDisabled && (restoreOvhDns = !restoreOvhDns)">
                <strong class="mb-2">Restore OVH DNS</strong>
                <small class="text-muted mb-2">Restore nameservers to OVH nameservers retrieved from the the domain's DNS zone</small>
                
                <!-- <div v-if="restoreOvhDns && dnsConfig && dnsConfig.ovh_nameservers" class="dns-preview mt-2" @click.stop> -->
                <div v-if="restoreOvhDns" class="dns-preview mt-2" @click.stop>
                  <div class="dns-record-type">Nameservers:</div>
                  <div class="dns-values">
                    <!-- <span v-for="(ns, idx) in dnsConfig.ovh_nameservers" :key="'ns-' + idx" class="dns-value">
                      {{ ns }}
                    </span> -->
                    <span class="dns-value">The nameservers retrieved from the OVH DNS zone associated with the domain will be used.</span>
                  </div>
                </div>
              </div>
            </template>
          </CInputCheckbox>
        </div>

        <!-- Use Custom Nameservers -->
        <div 
          class="operation-card mt-3" 
          :class="{ 'selected': useCustomNameservers, 'disabled': isUseCustomNameserversDisabled }"
        >
          <CInputCheckbox
            :checked.sync="useCustomNameservers"
            :custom="true"
            :disabled="isUseCustomNameserversDisabled"
            class="mb-0"
          >
            <template #label>
              <div class="d-flex flex-column" @click="!isUseCustomNameserversDisabled && (useCustomNameservers = !useCustomNameservers)">
                <strong class="mb-2">Use Custom Nameservers</strong>
                <small class="text-muted mb-2">Update nameservers to the configured custom nameservers</small>

                <div v-if="useCustomNameservers && dnsConfig && dnsConfig.custom_nameservers" class="dns-preview mt-2" @click.stop>
                  <div class="dns-record-type">Nameservers:</div>
                  <div class="dns-values">
                    <span v-for="(ns, idx) in dnsConfig.custom_nameservers" :key="'cns-' + idx" class="dns-value">
                      {{ ns }}
                    </span>
                  </div>
                </div>
              </div>
            </template>
          </CInputCheckbox>
        </div>
      </div>
    </div>

    <template #footer>
      <CButton color="link" @click="active = false">{{ allOperationsDisabled ? 'Close' : 'Cancel' }}</CButton>
      <CButton 
        v-if="!allOperationsDisabled"
        color="primary" 
        shape="pill"
        :disabled="submitted || (!pointToMenute && !restoreOvhDns && !useCustomNameservers) || loading" 
        @click="performOperations"
      >
        <CSpinner v-if="submitted" size="sm" /> Apply Changes
      </CButton>
    </template>
  </CModal>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "DnsManagementModal",
  props: {
    restaurant: {
      type: Object,
      default: null,
    },
    defaultOperation: {
      type: String,
      default: null, // 'point_to_menute' or 'restore_ovh'
    }
  },
  data() {
    return {
      pointToMenute: false,
      restoreOvhDns: false,
      useCustomNameservers: false,
      dnsConfig: null,
      loading: false,
      submitted: false,
      selectedIpv4: [],
      selectedIpv6: [],
    };
  },
  computed: {
    ...mapGetters(["errors"]),
    active: {
      get() { return this.$store.state.dnsManagementModalShow; },
      set(value) { this.$store.state.dnsManagementModalShow = value; }
    },
    
    isPointToMenuteDisabled() {
      return false;
    },
    selectedDomain() {
      return this.restaurant?.domain || this.restaurant?.website_etenonline_be || '';
    },
    
    isRestoreOvhDisabled() {
      if (!this.restaurant?.dns_check_result?.checks) return false;
      
      const nsRecord = this.restaurant.dns_check_result.checks.NS;
      
      // Disable if NS records are correct (pointing to OVH)
      return nsRecord?.status === 'correct' && nsRecord?.is_ovh === true;
    },
    isUseCustomNameserversDisabled() {
      // Custom nameservers seçeneği, diğer iki seçenekten biri seçiliyse devre dışı olmalı
      if (this.pointToMenute || this.restoreOvhDns) return true;

      if (!this.restaurant?.dns_check_result?.checks) return false;
      
      const nsRecord = this.restaurant.dns_check_result.checks.NS;
      return nsRecord?.is_custom_config === true;
    },
    
    allOperationsDisabled() {
      return this.isPointToMenuteDisabled && this.isRestoreOvhDisabled;
    },
  },
  watch: {
    active: async function (val) {
      if (val) {
        // Reset selections
        this.pointToMenute = false;
        this.restoreOvhDns = false;
        this.useCustomNameservers = false;
        this.selectedIpv4 = [];
        this.selectedIpv6 = [];
        
        // Set default operation based on which button was clicked
        if (this.defaultOperation === 'point_to_menute') {
          this.pointToMenute = true;
        } else if (this.defaultOperation === 'restore_ovh') {
          this.restoreOvhDns = true;
        } else if (this.defaultOperation === 'use_custom_ns') {
          this.useCustomNameservers = true;
        }
        
        // Load DNS configuration
        await this.loadDnsConfig();
      }
    },
    useCustomNameservers(newVal) {
      // Mutual exclusivity: custom seçildiyse diğerlerini kapat
      if (newVal) {
        this.pointToMenute = false;
        this.restoreOvhDns = false;
      }
    },
    pointToMenute(newVal) {
      if (newVal && this.dnsConfig) {
        this.applyDefaultIpSelection();
      } else if (!newVal) {
        this.selectedIpv4 = [];
        this.selectedIpv6 = [];
      }

      // Mutual exclusivity: point_to_menute seçildiyse custom kapat
      if (newVal) {
        this.useCustomNameservers = false;
      }
    },
    restoreOvhDns(newVal) {
      // Mutual exclusivity: restore_ovh seçildiyse custom kapat
      if (newVal) {
        this.useCustomNameservers = false;
      }
    }
  },
  methods: {
    async loadDnsConfig() {
      this.loading = true;
      try {
        const CACHE_KEY = "dnsConfig:v2";
        const cachedConfig = sessionStorage.getItem(CACHE_KEY);

        if (cachedConfig) {
          this.dnsConfig = JSON.parse(cachedConfig);
        } else {
          const { data } = await this.$axios.get(this.$backend.RESTAURANT.DNS_MANAGEMENT.GET_CONFIG);
          this.dnsConfig = data;
          sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
        }
      } catch (error) {
        this.$toast.error("Failed to load DNS configuration.");
        this.active = false;
      } finally {
        this.loading = false;
        if (this.pointToMenute && this.dnsConfig) {
          this.applyDefaultIpSelection();
        }
      }
    },
    
    async performOperations() {
      if (this.allOperationsDisabled) {
        this.$toast.info("All DNS records are already configured correctly.");
        return;
      }
      
      if (!this.pointToMenute && !this.restoreOvhDns && !this.useCustomNameservers) {
        this.$toast.warning("Please select at least one operation.");
        return;
      }

      if (this.pointToMenute && this.selectedIpv4.length === 0 && this.selectedIpv6.length === 0) {
        this.$toast.warning("Please select at least one IP address for A or AAAA records.");
        return;
      }

      this.submitted = true;
      
      try {
        const payload = {
          restaurant_id: this.restaurant.id,
          domain: this.selectedDomain,
          point_to_menute: this.pointToMenute,
          restore_ovh_dns: this.restoreOvhDns,
          use_custom_nameservers: this.useCustomNameservers,
          menute_ipv4: this.selectedIpv4,
          menute_ipv6: this.selectedIpv6,
        };
        const response = await this.$axios.post(this.$backend.RESTAURANT.DNS_MANAGEMENT.PERFORM_OPERATIONS, payload);
        
        this.$toast.success(response.data?.message);
        this.$emit('operations-completed');
        this.active = false;
      } catch (error) {
        this.$toast.error(typeof this.errors === 'object'
          ? Object.values(this.errors).filter(key => key !== false).join()
          : 'Failed to perform DNS operations.'
        );
      } finally {
        this.submitted = false;
      }
    },

    toggleIpSelection(selectionArray, ip) {
      const array = this[selectionArray];
      const index = array.indexOf(ip);
      if (index > -1) {
        array.splice(index, 1);
      } else {
        array.push(ip);
      }
    },

    applyDefaultIpSelection() {
      this.selectedIpv4 = this.getDefaultSelectedIps("menute_ipv4", "menute_ipv4_selected");
      this.selectedIpv6 = this.getDefaultSelectedIps("menute_ipv6", "menute_ipv6_selected");
    },

    getDefaultSelectedIps(recordsKey, selectedRecordsKey) {
      const records = this.dnsConfig?.[recordsKey] || [];
      const selectedRecords = this.dnsConfig?.[selectedRecordsKey];

      if (!Array.isArray(selectedRecords)) {
        return [...records];
      }

      return selectedRecords.filter((ip) => records.includes(ip));
    },
  },
};
</script>

<style scoped>
html:not([dir=rtl]) .operation-card .custom-control {
  padding-left: 0.25rem;
}

.operation-card {
  background-color: #f8f9fa;
  border: 2px solid #e1e1e1;
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.2s ease;
  cursor: pointer;
}

.operation-card:hover {
  background-color: #e9ecef;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.operation-card.selected {
  border-color: #007bff;
  background-color: rgba(0, 123, 255, 0.05);
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.operation-card.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
  background-color: #e9ecef;
}

.dns-preview {
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 0.75rem;
}

.dns-record-type {
  font-weight: 600;
  font-size: 0.875rem;
  color: #495057;
  margin-bottom: 0.5rem;
}

.dns-values {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.375rem;
  align-items: center;
}

.dns-value {
  font-family: monospace;
  font-size: 0.875rem;
  color: #212529;
  padding: 0.375rem 0.75rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #dee2e6;
  display: inline-block;
  width: fit-content;
}
</style>

<style>
/* --- Button-like Checkboxes --- */
.ip-checkbox .custom-control-label::before,
.ip-checkbox .custom-control-label::after {
  display: none;
}

.ip-checkbox .custom-control-label {
  padding: 0.375rem 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background-color: #f8f9fa;
  color: #212529;
  font-family: monospace;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.ip-checkbox .custom-control-label:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
}

.ip-checkbox .custom-control-input:checked + .custom-control-label {
  background-color: #0d6efd;
  color: #fff;
  border-color: #0d6efd;
}

.ip-checkbox .custom-control-input:focus + .custom-control-label {
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
}
</style>

